import React, { useState, useEffect, useCallback, useRef } from 'react';
import Seo from '../components/Seo';
import { seo } from '../seo/seoData';
import { useNavigate } from 'react-router-dom';
import {
  Car, Power, MapPin, Users, Phone,
  Navigation, Play, Flag, History,
  Loader2, RefreshCw, LogOut, CheckCircle2, Calendar,
  Wallet, Signal, Sun, Moon
} from 'lucide-react';
import axios from 'axios';
// G1: driver-local tier badge (eco / business / van; G3.E removed PRIORITY).
// Deliberately driver-local; do NOT import AdminV3 TierBadge here.
import DriverTierBadge from '../components/driver/DriverTierBadge';
// Phase 4 G5 — Driver self-service compliance UI (Plan §2.2 M-F-20, M-F-21).
// Driver Dashboard remains the SINGLE AUTHORITATIVE self-service surface.
import DriverComplianceStatusPanel from '../components/DriverComplianceStatusPanel';
import DriverDocumentsSelfServicePanel from '../components/DriverDocumentsSelfServicePanel';

const API_URL = '/api';
const cn = (...classes) => classes.filter(Boolean).join(' ');

const themes = {
  dark: {
    bg:       'bg-[#0a0f1e]',
    bgCard:   'bg-slate-900/80',
    bgSubtle: 'bg-white/5',
    border:   'border-white/8',
    borderSub:'border-white/5',
    text:     'text-white',
    textSub:  'text-slate-400',
    textMuted:'text-slate-500',
    header:   'bg-[#0a0f1e]/95 border-white/5',
    divider:  'divide-white/5',
    mono:     'bg-white/8 text-slate-400',
    accent:   'text-amber-400',
    accentBg: 'bg-amber-400/10 border-amber-400/20 text-amber-400',
    tabActive:'bg-white/10 text-white',
    tabInact: 'text-slate-500',
  },
  light: {
    bg:       'bg-slate-50',
    bgCard:   'bg-white',
    bgSubtle: 'bg-slate-100',
    border:   'border-slate-200',
    borderSub:'border-slate-100',
    text:     'text-slate-900',
    textSub:  'text-slate-500',
    textMuted:'text-slate-400',
    header:   'bg-white/95 border-slate-200',
    divider:  'divide-slate-100',
    mono:     'bg-slate-100 text-slate-500',
    accent:   'text-amber-600',
    accentBg: 'bg-amber-50 border-amber-200 text-amber-700',
    tabActive:'bg-slate-200 text-slate-900',
    tabInact: 'text-slate-400',
  },
};

const statusMeta = {
  assigned:        { label:'Assignée',  dColor:'text-blue-400',   dBg:'bg-blue-500/15',   dBorder:'border-blue-500/30',  lColor:'text-blue-700',   lBg:'bg-blue-50',   lBorder:'border-blue-200',  dBtn:'bg-blue-500/15 border-blue-500/30 text-blue-400 hover:bg-blue-500/25',    lBtn:'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' },
  acknowledged:    { label:'Confirmée', dColor:'text-cyan-400',   dBg:'bg-cyan-500/15',   dBorder:'border-cyan-500/30',  lColor:'text-cyan-700',   lBg:'bg-cyan-50',   lBorder:'border-cyan-200',  dBtn:'bg-amber-500/15 border-amber-500/30 text-amber-400 hover:bg-amber-500/25', lBtn:'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100' },
  arriving:        { label:'En route',  dColor:'text-amber-400',  dBg:'bg-amber-500/15',  dBorder:'border-amber-500/30', lColor:'text-amber-700',  lBg:'bg-amber-50',  lBorder:'border-amber-200', dBtn:'bg-emerald-500/15 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/25', lBtn:'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100' },
  driver_arriving: { label:'En route',  dColor:'text-amber-400',  dBg:'bg-amber-500/15',  dBorder:'border-amber-500/30', lColor:'text-amber-700',  lBg:'bg-amber-50',  lBorder:'border-amber-200', dBtn:'bg-emerald-500/15 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/25', lBtn:'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100' },
  in_progress:     { label:'En cours',  dColor:'text-emerald-400',dBg:'bg-emerald-500/15',dBorder:'border-emerald-500/30',lColor:'text-emerald-700',lBg:'bg-emerald-50',lBorder:'border-emerald-200',dBtn:'bg-white/10 border-white/20 text-white hover:bg-white/15',                      lBtn:'bg-slate-900 border-slate-700 text-white hover:bg-slate-800' },
  completed:       { label:'Terminée',  dColor:'text-slate-400',  dBg:'bg-slate-500/15',  dBorder:'border-slate-500/30', lColor:'text-slate-500',  lBg:'bg-slate-100', lBorder:'border-slate-200', dBtn:'', lBtn:'' },
};

const actionMeta = {
  acknowledge: { status:'assigned',    label:'Confirmer la course',                icon: CheckCircle2, loadKey:'acknowledge' },
  arriving:    { status:'acknowledged',label:"J'arrive au point de prise en charge",icon: Navigation,   loadKey:'arriving' },
  start:       { status:'arriving',    label:'Démarrer la course',                 icon: Play,         loadKey:'start' },
  complete:    { status:'in_progress', label:'Terminer la course',                 icon: Flag,         loadKey:'complete' },
};

export default function DriverDashboard() {
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(() => {
    const s = localStorage.getItem('driverTheme');
    return s ? s === 'dark' : (new Date().getHours() >= 20 || new Date().getHours() < 7);
  });
  const [driver, setDriver]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [assignedRides, setAssignedRides] = useState([]);
  const [activeRide, setActiveRide]   = useState(null);
  const [rideHistory, setRideHistory] = useState([]);
  const [isTracking, setIsTracking]   = useState(false);
  const [earnings, setEarnings]       = useState({ today:0, week:0, month:0 });
  const [activeTab, setActiveTab]     = useState('dashboard');
  const locRef = useRef(null);
  const token  = localStorage.getItem('driverToken');
  const t      = isDark ? themes.dark : themes.light;

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem('driverTheme', next ? 'dark' : 'light');
  };

  const api = useCallback(() => axios.create({
    baseURL: API_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const fetchRides = useCallback(async () => {
    try {
      const { data: rides } = await api().get('/driver/rides');
      const active = rides.find(r => ['assigned','acknowledged','driver_arriving','arriving','in_progress'].includes(r.status));
      setActiveRide(active || null);
      setAssignedRides(rides.filter(r => ['assigned','acknowledged'].includes(r.status) && r.id !== active?.id));
      setRideHistory(rides.filter(r => r.status === 'completed'));
      const now = new Date(), t0 = now.toISOString().split('T')[0];
      const t7  = new Date(now-7*864e5).toISOString().split('T')[0];
      const t30 = new Date(now-30*864e5).toISOString().split('T')[0];
      const done = rides.filter(r => r.status === 'completed');
      const earn = r => (r.final_price||r.estimated_price||0)*.9;
      setEarnings({
        today: done.filter(r=>r.date===t0).reduce((s,r)=>s+earn(r),0),
        week:  done.filter(r=>r.date>=t7).reduce((s,r)=>s+earn(r),0),
        month: done.filter(r=>r.date>=t30).reduce((s,r)=>s+earn(r),0),
      });
    } catch {}
  }, [api]);

  const fetchDriverInfo = useCallback(async () => {
    try { const { data } = await api().get('/driver/me'); setDriver(data); }
    catch { localStorage.removeItem('driverToken'); navigate('/driver/login'); }
    finally { setLoading(false); }
  }, [api, navigate]);

  useEffect(() => { if (!token) { navigate('/driver/login'); return; } fetchDriverInfo(); }, [token, navigate, fetchDriverInfo]);
  useEffect(() => { if (driver) fetchRides(); }, [driver, fetchRides]);
  useEffect(() => {
    if (driver?.status !== 'available') return;
    const iv = setInterval(fetchRides, 30000);
    return () => clearInterval(iv);
  }, [driver?.status, fetchRides]);

  useEffect(() => {
    if (driver?.status === 'available' && navigator.geolocation) {
      setIsTracking(true);
      locRef.current = navigator.geolocation.watchPosition(
        ({ coords: { latitude:lat, longitude:lng, heading, speed, accuracy } }) =>
          api().post('/tracking/driver/location', { latitude:lat, longitude:lng, heading, speed:speed?speed*3.6:null, accuracy }).catch(()=>{}),
        () => setIsTracking(false),
        { enableHighAccuracy:true, timeout:10000, maximumAge:5000 }
      );
    } else {
      if (locRef.current) navigator.geolocation.clearWatch(locRef.current);
      setIsTracking(false);
    }
    return () => { if (locRef.current) navigator.geolocation.clearWatch(locRef.current); };
  }, [driver?.status, api]);

  const toggleStatus = async () => {
    const s = driver.status === 'available' ? 'offline' : 'available';
    try { await api().patch('/driver/status', { status:s }); setDriver(p=>({...p,status:s})); if(s==='available') fetchRides(); } catch {}
  };

  const handleAction = async (action, rideId) => {
    setActionLoading(action);
    try {
      const map = { acknowledge:`/driver/rides/${rideId||activeRide?.id}/acknowledge`, arriving:`/driver/rides/${activeRide?.id}/arriving`, start:`/driver/rides/${activeRide?.id}/start`, complete:`/driver/rides/${activeRide?.id}/complete` };
      const { data } = await api().post(map[action]);
      if (action === 'complete') { setActiveRide(null); fetchRides(); } else setActiveRide(data);
    } catch(e) { alert(e.response?.data?.detail||'Erreur'); }
    finally { setActionLoading(null); }
  };

  const handleLogout = async () => {
    try { await api().post('/driver/logout'); } catch {}
    localStorage.removeItem('driverToken'); localStorage.removeItem('driverInfo');
    navigate('/driver/login');
  };

  const StatusBadge = ({ status }) => {
    const m = statusMeta[status] || statusMeta.assigned;
    return <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold border', isDark?m.dBg:m.lBg, isDark?m.dColor:m.lColor, isDark?m.dBorder:m.lBorder)}>{m.label}</span>;
  };

  if (loading) return (
    <div className={cn('min-h-screen flex items-center justify-center', t.bg)}>
      <div className="flex flex-col items-center gap-3">
        <div className={cn('h-10 w-10 rounded-full border-2 border-t-transparent animate-spin', isDark?'border-amber-400':'border-amber-600')} />
        <span className={cn('text-sm', t.textMuted)}>Chargement...</span>
      </div>
    </div>
  );

  const isOnline = driver?.status === 'available';

  return (
    <div className={cn('min-h-screen transition-colors duration-300', t.bg, t.text)}>
      <Seo {...seo.driver} />

      {/* HEADER */}
      <header className={cn('sticky top-0 z-50 backdrop-blur border-b transition-colors duration-300', t.header)}>
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[15px] font-bold tracking-tight">BLACK MATTE</div>
            <div className={cn('text-[10px] uppercase tracking-widest', t.textMuted)}>Chauffeur</div>
          </div>
          <div className="flex items-center gap-2">
            {/* GPS pill */}
            <div className={cn('flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium border',
              isTracking
                ? (isDark ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border-emerald-200')
                : (isDark ? 'bg-slate-800 text-slate-500 border-slate-700' : 'bg-slate-100 text-slate-400 border-slate-200'))}>
              <Signal className="h-3 w-3" />GPS
            </div>
            {/* Online toggle */}
            <button onClick={toggleStatus}
              className={cn('flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border transition-all',
                isOnline
                  ? (isDark ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border-emerald-200')
                  : (isDark ? 'bg-slate-800 text-slate-400 border-slate-700' : 'bg-slate-100 text-slate-500 border-slate-200'))}>
              <div className={cn('h-2 w-2 rounded-full', isOnline ? 'bg-emerald-400 animate-pulse' : (isDark?'bg-slate-600':'bg-slate-300'))} />
              {isOnline ? 'En ligne' : 'Hors ligne'}
            </button>
            {/* Theme toggle */}
            <button onClick={toggleTheme}
              className={cn('rounded-full p-2 transition-colors', isDark?'text-slate-400 hover:text-amber-400 hover:bg-white/5':'text-slate-400 hover:text-amber-600 hover:bg-slate-100')}>
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {/* Logout */}
            <button onClick={handleLogout}
              className={cn('rounded-full p-2 transition-colors', isDark?'text-slate-500 hover:text-white hover:bg-white/5':'text-slate-400 hover:text-slate-700 hover:bg-slate-100')}>
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-4 space-y-4 pb-10">

        {/* DRIVER CARD */}
        <div className={cn('rounded-2xl border p-4 transition-colors duration-300', t.bgCard, t.border)}>
          <div className="flex items-center gap-3">
            <div className={cn('flex h-12 w-12 items-center justify-center rounded-xl border text-lg font-bold', t.accentBg)}>
              {driver?.name?.split(' ').map(n=>n[0]).join('').slice(0,2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[15px] truncate">{driver?.name}</div>
              <div className={cn('text-xs truncate', t.textSub)}>{driver?.vehicle_info||'Véhicule non renseigné'}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              {driver?.subscription_active && (
                <span className={cn('text-[10px] font-semibold rounded-full px-2 py-0.5 border',
                  isDark?'bg-blue-500/10 text-blue-400 border-blue-500/20':'bg-blue-50 text-blue-600 border-blue-200')}>Abonné</span>
              )}
              <span className={cn('text-[10px]', t.textMuted)}>{rideHistory.length} courses</span>
            </div>
          </div>
        </div>

        {/* EARNINGS */}
        <div className="grid grid-cols-3 gap-2">
          {[{label:"Aujourd'hui",v:earnings.today,ac:false},{label:'Cette semaine',v:earnings.week,ac:false},{label:'Ce mois',v:earnings.month,ac:true}].map(({label,v,ac})=>(
            <div key={label} className={cn('rounded-xl border p-3 text-center transition-colors duration-300', t.bgCard, t.border)}>
              <div className={cn('text-[10px] mb-1', t.textMuted)}>{label}</div>
              <div className={cn('text-lg font-bold', ac?t.accent:t.text)}>{v.toFixed(0)}€</div>
            </div>
          ))}
        </div>

        {/* COMPLIANCE — Phase 4 G5 (Plan §2.2 M-F-20, M-F-21) */}
        {token && <DriverComplianceStatusPanel token={token} theme={t} />}
        {token && <DriverDocumentsSelfServicePanel token={token} theme={t} />}

        {/* TABS */}
        <div className={cn('flex gap-1 rounded-xl border p-1 transition-colors duration-300', t.bgCard, t.border)}>
          {[{v:'dashboard',l:'Accueil',I:Car},{v:'history',l:'Historique',I:History}].map(({v,l,I})=>(
            <button key={v} onClick={()=>setActiveTab(v)}
              className={cn('flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium transition-all',
                activeTab===v ? t.tabActive : t.tabInact)}>
              <I className="h-4 w-4" />{l}
              {v==='dashboard'&&activeRide&&<span className="ml-1 h-2 w-2 rounded-full bg-emerald-400 animate-pulse"/>}
            </button>
          ))}
        </div>

        {/* DASHBOARD */}
        {activeTab==='dashboard'&&(
          <div className="space-y-4">

            {/* ACTIVE RIDE */}
            {activeRide&&(()=>{
              const m = statusMeta[activeRide.status]||statusMeta.assigned;
              return (
                <div className={cn('rounded-2xl border-2 overflow-hidden transition-colors duration-300', t.bgCard, isDark?m.dBorder:m.lBorder)}>
                  <div className={cn('flex items-center justify-between px-4 py-2.5', isDark?m.dBg:m.lBg)}>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"/>
                      <span className={cn('text-sm font-semibold', isDark?m.dColor:m.lColor)}>Course active</span>
                      <span className={cn('font-mono text-[11px] rounded px-2 py-0.5', t.mono)}>
                        {activeRide.booking_reference||activeRide.id.substring(0,8).toUpperCase()}
                      </span>
                      {/* G1: surface the customer-widget tier on the active ride card */}
                      <DriverTierBadge tier={activeRide.service_type}/>
                    </div>
                    <StatusBadge status={activeRide.status}/>
                  </div>
                  <div className="p-4 space-y-4">
                    {/* Route */}
                    <div className="space-y-3">
                      {[{icon:MapPin,color:isDark?'bg-emerald-500/20 text-emerald-400':'bg-emerald-100 text-emerald-600',label:'Départ',addr:activeRide.pickup_address},
                        {icon:MapPin,color:isDark?'bg-rose-500/20 text-rose-400':'bg-rose-100 text-rose-600',label:'Destination',addr:activeRide.destination_address}
                      ].map(({icon:I,color,label,addr},i)=>(
                        <React.Fragment key={i}>
                          {i===1&&<div className={cn('ml-3 border-l border-dashed h-3',isDark?'border-slate-700':'border-slate-300')}/>}
                          <div className="flex items-start gap-3">
                            <div className={cn('mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full',color)}>
                              <I className="h-3 w-3"/>
                            </div>
                            <div>
                              <div className={cn('text-[10px] uppercase tracking-wider',t.textMuted)}>{label}</div>
                              <div className={cn('text-sm',t.text)}>{addr}</div>
                            </div>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                    {/* Info grid */}
                    <div className="grid grid-cols-3 gap-2">
                      {[{I:Calendar,top:activeRide.date,bot:activeRide.time},{I:Users,top:activeRide.passengers,bot:'passager(s)'},{I:Wallet,top:`${activeRide.estimated_price}€`,bot:null,ac:true}].map(({I,top,bot,ac},i)=>(
                        <div key={i} className={cn('rounded-xl p-3 text-center',t.bgSubtle)}>
                          <I className={cn('h-4 w-4 mx-auto mb-1',t.textMuted)}/>
                          <div className={cn('text-sm font-medium',ac?t.accent:t.text)}>{top}</div>
                          {bot&&<div className={cn('text-[11px]',t.textMuted)}>{bot}</div>}
                        </div>
                      ))}
                    </div>
                    {/* Client */}
                    <div className={cn('flex items-center justify-between rounded-xl px-4 py-3',t.bgSubtle)}>
                      <div className="flex items-center gap-2">
                        <Phone className={cn('h-4 w-4',t.textSub)}/>
                        <span className={cn('text-sm',t.text)}>{activeRide.phone}</span>
                      </div>
                      <a href={`tel:${activeRide.phone}`}
                        className={cn('rounded-lg px-3 py-1.5 text-xs font-semibold border',
                          isDark?'bg-emerald-500/15 border-emerald-500/30 text-emerald-400':'bg-emerald-50 border-emerald-200 text-emerald-700')}>
                        Appeler
                      </a>
                    </div>
                    {/* Action button */}
                    {Object.entries(actionMeta).map(([action,{status,label,icon:I,loadKey}])=>
                      activeRide.status===status&&(
                        <button key={action} onClick={()=>handleAction(action,activeRide.id)} disabled={!!actionLoading}
                          className={cn('w-full flex items-center justify-center gap-2 rounded-2xl font-semibold py-4 text-[15px] disabled:opacity-50 transition border',
                            isDark?m.dBtn:m.lBtn)}>
                          {actionLoading===loadKey?<Loader2 className="h-5 w-5 animate-spin"/>:<><I className="h-5 w-5"/>{label}</>}
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })()}

            {/* UPCOMING */}
            {assignedRides.length>0&&(
              <div className={cn('rounded-2xl border transition-colors duration-300',t.bgCard,t.border)}>
                <div className={cn('flex items-center justify-between px-4 py-3 border-b',t.borderSub)}>
                  <span className="text-sm font-semibold">Prochaines courses</span>
                  <span className={cn('rounded-full text-[11px] font-semibold px-2.5 py-1 border',
                    isDark?'bg-blue-500/15 text-blue-400 border-blue-500/20':'bg-blue-50 text-blue-600 border-blue-200')}>{assignedRides.length}</span>
                </div>
                <div className={cn('divide-y',t.divider)}>
                  {assignedRides.map(ride=>(
                    <div key={ride.id} className="flex items-center gap-3 px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={cn('font-mono text-[11px] rounded px-2 py-0.5',t.mono)}>
                            {ride.booking_reference||ride.id.substring(0,8).toUpperCase()}
                          </span>
                          {/* G1: tier badge per upcoming-ride row */}
                          <DriverTierBadge tier={ride.service_type}/>
                          <span className={cn('text-[11px]',t.textMuted)}>{ride.date}·{ride.time}</span>
                        </div>
                        <div className={cn('text-xs truncate',t.textSub)}>
                          {ride.pickup_address?.split(',')[0]} → {ride.destination_address?.split(',')[0]}
                        </div>
                      </div>
                      <div className={cn('text-base font-bold flex-shrink-0',t.accent)}>{ride.estimated_price}€</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* EMPTY online */}
            {!activeRide&&assignedRides.length===0&&isOnline&&(
              <div className={cn('rounded-2xl border p-8 text-center transition-colors duration-300',t.bgCard,t.border)}>
                <div className={cn('mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl',t.bgSubtle)}>
                  <Car className={cn('h-7 w-7',t.textMuted)}/>
                </div>
                <div className="font-semibold mb-1">Aucune course assignée</div>
                <div className={cn('text-sm mb-4',t.textSub)}>Les courses vous seront assignées par l'administration</div>
                <button onClick={fetchRides} className={cn('inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm transition',t.bgSubtle,t.border,t.textSub)}>
                  <RefreshCw className="h-4 w-4"/>Actualiser
                </button>
              </div>
            )}

            {/* EMPTY offline */}
            {!isOnline&&!activeRide&&(
              <div className={cn('rounded-2xl border p-8 text-center transition-colors duration-300',t.bgCard,t.border)}>
                <div className={cn('mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl',t.bgSubtle)}>
                  <Power className={cn('h-7 w-7',t.textMuted)}/>
                </div>
                <div className="font-semibold mb-1">Vous êtes hors ligne</div>
                <div className={cn('text-sm mb-4',t.textSub)}>Passez en ligne pour recevoir des courses</div>
                <button onClick={toggleStatus}
                  className={cn('inline-flex items-center gap-2 rounded-xl border px-5 py-2.5 text-sm font-semibold transition',
                    isDark?'bg-emerald-500/15 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/25':'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100')}>
                  <Power className="h-4 w-4"/>Passer en ligne
                </button>
              </div>
            )}
          </div>
        )}

        {/* HISTORY */}
        {activeTab==='history'&&(
          <div className="space-y-3">
            {rideHistory.length===0?(
              <div className={cn('rounded-2xl border p-8 text-center',t.bgCard,t.border)}>
                <History className={cn('h-10 w-10 mx-auto mb-3',t.textMuted)}/>
                <div className={cn('text-sm',t.textSub)}>Aucune course terminée</div>
              </div>
            ):rideHistory.slice(0,20).map(ride=>(
              <div key={ride.id} className={cn('rounded-2xl border p-4 transition-colors duration-300',t.bgCard,t.border)}>
                <div className="flex items-center justify-between mb-3">
                  <span className={cn('font-mono text-[11px] rounded px-2 py-0.5',t.mono)}>
                    {ride.booking_reference||ride.id.substring(0,8).toUpperCase()}
                  </span>
                  <span className={cn('text-base font-bold',t.accent)}>{ride.final_price||ride.estimated_price}€</span>
                </div>
                <div className="space-y-1.5 mb-3">
                  <div className="flex items-start gap-2">
                    <div className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-emerald-500"/>
                    <span className={cn('text-xs',t.textSub)}>{ride.pickup_address}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-rose-500"/>
                    <span className={cn('text-xs',t.textSub)}>{ride.destination_address}</span>
                  </div>
                </div>
                <div className={cn('text-[11px]',t.textMuted)}>{ride.date} · {ride.time}</div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
